import {
  Combobox,
  DropdownButton,
  NoOptionsPlaceholder
} from '@eggplant/pattern-library'
import { FiPlus } from 'react-icons/fi'
import { GetModelSuiteMonitorConfigsRequest } from '@eggplant/api-requests'
import { HiOutlineDocumentText } from 'react-icons/hi'
import {
  LIVE_RUN,
  ONLY_LIVE_RUN_TOOLTIP,
  ONLY_TEST_CONFIG_TOOLTIP,
  TEST_CONFIG
} from './constants'
import {
  LinkToAddTestConfig,
  useConfigTypeTitle,
  useModule,
  useSpaceAccess
} from '@eggplant/routing'
import type {
  ButtonShape,
  DropdownPlacement,
  GetFilterIdListResponse,
  GetModelSuiteMonitorConfigsRequestParams,
  Options
} from '@eggplant/types'

interface Props {
  onChange: (value: string) => void
  placeholder?: string
  value?: string
  dropdownPlacement?: DropdownPlacement
  isFilterFixedMinMaxWidth?: boolean
}
interface labelValue {
  label: string
  value: string
}

const initialOptions = [
  {
    label: ONLY_LIVE_RUN_TOOLTIP,
    value: LIVE_RUN
  },
  {
    label: ONLY_TEST_CONFIG_TOOLTIP,
    value: TEST_CONFIG
  }
]

const getOptions = (items: GetFilterIdListResponse[] | undefined, nonSearchableOptions?: labelValue[]) => {
  if (!Array.isArray(items)) return []

  const options = items.map(item => ({
    label: item.name,
    value: item.id_list.join(',')
  }))

  return nonSearchableOptions
    ? options.concat(nonSearchableOptions)
    : options
}

const SelectModelSuiteTestConfigs = ({
  onChange,
  isFilterFixedMinMaxWidth = true,
  placeholder = 'Test config / Live run',
  value,
  dropdownPlacement = 'bottom'
}: Props) => {
  const [isAccessible] = useSpaceAccess()
  const configTypeText = useConfigTypeTitle()
  const [isMonitorFlag] = useModule()

  const params = { type: configTypeText } as GetModelSuiteMonitorConfigsRequestParams

  // eslint-disable-next-line complexity
  const renderButton = (options: Options) => {
    const currentOption = value
      ? options.find(item => item.value.includes(value))
      : undefined

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const testconfigTooltip = [LIVE_RUN, TEST_CONFIG].includes(String(value))
      ? label
      : `Test config: ${label}`

    const tooltip = currentOption?.value
      ? testconfigTooltip
      : placeholder

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        Icon={HiOutlineDocumentText}
        dropdownPlacement={dropdownPlacement}
        isFilter
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        isHighlighted={!!value}
        label={label}
        title={tooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={<HiOutlineDocumentText size='24px' />}
      linkTo={
        isAccessible
          ? (
            <LinkToAddTestConfig>
              <FiPlus
                aria-hidden
                size={16}
              />
              New Test Config
            </LinkToAddTestConfig>
          )
          : undefined
      }
      title='No test config found'
    />

  return (
    <GetModelSuiteMonitorConfigsRequest
      params={params}
      shortErrorMessage={`Failed to load ${configTypeText} config`}
    >
      {
        (data: GetFilterIdListResponse[]) =>
          <Combobox
            dropdownPlacement={dropdownPlacement}
            groupedOptions={
              [{
                label: `Available ${configTypeText} configs`,
                options: getOptions(data)
              }]
            }
            initialOptions={
              !isMonitorFlag
                ? initialOptions
                : []
            }
            isFilter
            isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
            noOptionsPlaceholder={noDataPlaceholder}
            onChange={onChange}
            renderButton={
              renderButton(
                getOptions(
                  data,
                  !isMonitorFlag
                    ? initialOptions
                    : []
                )
              )
            }
            value={value}
          />
      }
    </GetModelSuiteMonitorConfigsRequest>
  )
}

export default SelectModelSuiteTestConfigs
